import { useMemo } from 'react';
import { normalizeBlogPosts } from '../components/blog/blogPostsData';
import { buildBlogPostsSlugIndex } from '../seo/urlConfig';
import { useContent } from './ContentProvider';
import { STATIC_SITE_DEFAULTS } from './staticDefaults';
import { buildCaseStudyListFilters } from './caseStudyCategories';
import { normalizeCaseStudyDetail } from './caseStudyDetailTemplate';

export function useSiteContent() {
  const { siteContent } = useContent();
  return siteContent ?? STATIC_SITE_DEFAULTS;
}

export function useServicesList() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.services?.list ?? STATIC_SITE_DEFAULTS.services.list,
    [siteContent, tick]
  );
}

export function useServiceDetailsMap() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.services?.details ?? STATIC_SITE_DEFAULTS.services.details,
    [siteContent, tick]
  );
}

export function useServiceBySlug(slug) {
  const list = useServicesList();
  return useMemo(() => list.find((s) => s.slug === slug) ?? null, [list, slug]);
}

export function useServiceDetailPage(slug) {
  const details = useServiceDetailsMap();
  return useMemo(() => (slug ? details[slug] ?? null : null), [details, slug]);
}

export function useCaseStudyCards() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.caseStudies?.cards ?? STATIC_SITE_DEFAULTS.caseStudies.cards,
    [siteContent, tick]
  );
}

export function useCaseStudyStats() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.caseStudies?.stats ?? STATIC_SITE_DEFAULTS.caseStudies.stats,
    [siteContent, tick]
  );
}

export function useCaseStudyDetail(slug) {
  const { siteContent, tick } = useContent();
  return useMemo(() => {
    const details = siteContent?.caseStudies?.details ?? STATIC_SITE_DEFAULTS.caseStudies.details;
    const raw = slug ? details[slug] : null;
    return raw ? normalizeCaseStudyDetail(raw, slug) : null;
  }, [siteContent, tick, slug]);
}

export function useCaseStudyCategories() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.caseStudies?.categories ?? STATIC_SITE_DEFAULTS.caseStudies.categories,
    [siteContent, tick]
  );
}

export function useCaseStudyFilters() {
  const categories = useCaseStudyCategories();
  return useMemo(() => buildCaseStudyListFilters(categories), [categories]);
}

export function useBlogPosts() {
  const { siteContent, tick } = useContent();
  return useMemo(() => {
    const raw = siteContent?.blogs?.posts ?? STATIC_SITE_DEFAULTS.blogs.posts;
    return normalizeBlogPosts(raw);
  }, [siteContent, tick]);
}

export function useBlogPostsById() {
  const posts = useBlogPosts();
  return useMemo(
    () => Object.fromEntries(posts.map((p) => [String(p.id), p])),
    [posts]
  );
}

export function useBlogPostsBySlug() {
  const posts = useBlogPosts();
  return useMemo(() => buildBlogPostsSlugIndex(posts), [posts]);
}

export function useBlogFeaturedIdsByTopic() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () =>
      siteContent?.blogs?.featuredIdsByTopic ?? STATIC_SITE_DEFAULTS.blogs.featuredIdsByTopic,
    [siteContent, tick]
  );
}

export function useBlogCategories() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.blogs?.categories ?? STATIC_SITE_DEFAULTS.blogs.categories,
    [siteContent, tick]
  );
}

export function useBlogBrowseTopicKeys() {
  const categories = useBlogCategories();
  return useMemo(() => categories.map((c) => c.key), [categories]);
}

export function useBlogVideoPool() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.blogs?.videoPool ?? STATIC_SITE_DEFAULTS.blogs.videoPool,
    [siteContent, tick]
  );
}

export function useTeamMembers() {
  const { siteContent, tick } = useContent();
  return useMemo(() => siteContent?.team ?? STATIC_SITE_DEFAULTS.team, [siteContent, tick]);
}

export function useHomeTestimonials() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.testimonials?.items ?? STATIC_SITE_DEFAULTS.testimonials.items,
    [siteContent, tick]
  );
}

export function useServicePagesList() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.servicePages?.list ?? STATIC_SITE_DEFAULTS.servicePages.list,
    [siteContent, tick]
  );
}

export function useServicePagesMap() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.servicePages?.pages ?? STATIC_SITE_DEFAULTS.servicePages.pages,
    [siteContent, tick]
  );
}

export function useServicePageBySlug(slug) {
  const pages = useServicePagesMap();
  return useMemo(() => (slug ? pages[slug] ?? null : null), [pages, slug]);
}

export function useAboutPageContent() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.aboutPage ?? STATIC_SITE_DEFAULTS.aboutPage,
    [siteContent, tick]
  );
}

export function useServicesPageContent() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.servicesPage ?? STATIC_SITE_DEFAULTS.servicesPage,
    [siteContent, tick]
  );
}

export function useHomePageContent() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.homePage ?? STATIC_SITE_DEFAULTS.homePage,
    [siteContent, tick]
  );
}

/** Convenience: just the `data` half of useHomePageContent(), for sections that only need content (no style overrides). */
export function useHomePageData() {
  return useHomePageContent().data;
}

export function useCaseStudiesPageContent() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.caseStudiesPage ?? STATIC_SITE_DEFAULTS.caseStudiesPage,
    [siteContent, tick]
  );
}

export function useCaseStudiesPageData() {
  return useCaseStudiesPageContent().data;
}

export function useContactPageContent() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.contactPage ?? STATIC_SITE_DEFAULTS.contactPage,
    [siteContent, tick]
  );
}

export function useContactPageData() {
  return useContactPageContent().data;
}

export function useSolutionVideoSources() {
  const { siteContent, tick } = useContent();
  return useMemo(
    () => siteContent?.media?.solutionVideos ?? STATIC_SITE_DEFAULTS.media.solutionVideos,
    [siteContent, tick]
  );
}
